import React, { useCallback, useLayoutEffect, useRef } from 'react';
import {
  AE_EDITABLE_INPUT,
  AE_EDITABLE_TEXTAREA,
  AE_SECTION_TITLE,
} from '@/components/assessment-engine/layout/aeTheme';

interface InlineEditableProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  variant?: 'title' | 'heading' | 'body' | 'legend';
  placeholder?: string;
  rows?: number;
}

const variantClass: Record<NonNullable<InlineEditableProps['variant']>, string> = {
  title: `${AE_SECTION_TITLE} ${AE_EDITABLE_INPUT}`,
  heading: `${AE_SECTION_TITLE} text-base ${AE_EDITABLE_INPUT}`,
  body: `${AE_EDITABLE_TEXTAREA} text-sm min-h-[3rem]`,
  legend: `${AE_EDITABLE_TEXTAREA} font-medium text-sm min-h-[2.5rem] resize-none overflow-hidden`,
};

/** Match textarea height to wrapped question text (no extra blank rows). */
function useAutoResizeTextarea(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const syncHeight = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useLayoutEffect(() => {
    syncHeight();
  }, [value, syncHeight]);

  return { ref, syncHeight };
}

/** Dotted-underline field that looks like respondent-facing content while editing. */
export const InlineEditable: React.FC<InlineEditableProps> = ({
  value,
  onChange,
  readOnly,
  variant = 'body',
  placeholder,
  rows = 3,
}) => {
  const { ref: legendRef, syncHeight: syncLegendHeight } = useAutoResizeTextarea(value);

  if (readOnly) {
    if (variant === 'title' || variant === 'heading') {
      return variant === 'title' ? (
        <h1 className={AE_SECTION_TITLE}>{value || placeholder}</h1>
      ) : (
        <h2 className={AE_SECTION_TITLE}>{value || placeholder}</h2>
      );
    }
    return value ? (
      <p className="text-sm font-medium text-[var(--color-surface-text)] whitespace-pre-wrap w-full">
        {value}
      </p>
    ) : null;
  }

  if (variant === 'body') {
    return (
      <textarea
        className={variantClass.body}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (variant === 'legend') {
    return (
      <textarea
        ref={legendRef}
        className={variantClass.legend}
        rows={1}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          syncLegendHeight();
        }}
      />
    );
  }

  return (
    <input
      className={variantClass[variant]}
      value={value}
      placeholder={placeholder}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    />
  );
};
